import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { User } from '@/lib/types/User'
import { ref } from 'vue'
import {
  initRecharge,
  listSubscriptions,
  loginShopifyAppProxy,
  getCDNProducts,
  getCDNProduct,
  createSubscription as rechargeCreateSubscription,
  createOnetime as rechargeCreateOnetime,
  type Session,
} from '@rechargeapps/storefront-client'

initRecharge({
  storeIdentifier: 'bower-collective.myshopify.com',
  storefrontAccessToken:
    'strfnt_ad9fc3d206617d23a67b3d6e49553a2c0aa362007ac0cffe18ff3074cbbbcc4a',
  appName: 'BowerCollective::PurchaseComponent',
  appVersion: '1.0.0',
  loginRetryFn: async () => {
    const session = await loginShopifyAppProxy()
    rechargeSession = session
    return session
  },
})

let rechargeSession: Session | undefined

const authenticate = async () => {
  if (rechargeSession) return rechargeSession
  rechargeSession = await loginShopifyAppProxy()
  return rechargeSession
}

const user = ref<User>({
  isLoggedIn: !!window.__BOWER__.customer,
  isSubscriber:
    !!window.__BOWER__.customer && window.__BOWER__.customer.isSubscriber,
  schedule: [],
  subscriptions: [],
  loadState:
    !!window.__BOWER__.customer && window.__BOWER__.customer.isSubscriber
      ? 'LOADING'
      : 'NOT_REQUIRED',
})

const initUser = () => {
  authenticate().then(async (s) => {
    if (s.customerId) {
      user.value.loadState = 'LOADING'
      const subscriptions = await listSubscriptions(s, {
        limit: 50,
        sort_by: 'id-asc',
        status: 'active',
      })
      user.value.isSubscriber = subscriptions.subscriptions.length > 0
      user.value.subscriptions = subscriptions.subscriptions
      user.value.loadState = 'DONE'
    }
  })
}

initUser()

export function getUser() {
  return user
}

export function getSavedState() {
  let savedState = {}
  try {
    const stateFromUrl = JSON.parse(
      new URLSearchParams(window.location.search).get(
        'purchase_data',
      ) as string,
    )
    if (stateFromUrl) {
      savedState = stateFromUrl
    }
  } catch (e) {
    // ignore error
  }
  return savedState
}

export function loginAndReturnHere(
  purchaseData: PurchaseData | { id: string } | '',
) {
  const returnUrl = `${window.location.pathname}?purchase_data=${JSON.stringify(
    purchaseData,
  )}`
  window.location.href = `/account/login?return_url=${encodeURIComponent(
    returnUrl,
  )}`
}

export async function createSubscription(purchaseData: PurchaseData) {
  if (!rechargeSession) throw new Error('No session')

  const addressId = parseInt(purchaseData.customerAddressId!)
  if (Number.isNaN(addressId)) throw new Error('No address ID')

  const properties = (
    purchaseData.attribution
      ? [
          {
            name: '_source',
            value: purchaseData.attribution.source,
          },
          {
            name: '_attribution',
            value: purchaseData.attribution.view,
          },
        ]
      : []
  ).concat(
    purchaseData.properties
      ? Object.entries(purchaseData.properties).map(([key, value]) => ({
          name: key,
          value,
        }))
      : [],
  )

  if (!purchaseData.selectedSellingPlanId) {
    return await rechargeCreateOnetime(rechargeSession, {
      address_id: addressId,
      external_variant_id: { ecommerce: purchaseData.selectedVariantId },
      external_product_id: { ecommerce: purchaseData.id },
      quantity: purchaseData.quantity,
      next_charge_scheduled_at: purchaseData.customerNextChargeDate,
      properties,
    })
  }

  const product = await getCDNProduct(purchaseData.id, { version: '2022-06' })
  const sellingPlan = product.plans.find(
    (plan) =>
      plan.external_plan_id?.toString() === purchaseData.selectedSellingPlanId,
  )

  if (!sellingPlan) throw new Error('Selling plan not found')

  return await rechargeCreateSubscription(
    rechargeSession,
    {
      plan_id: sellingPlan.id,
      address_id: addressId,
      external_variant_id: { ecommerce: purchaseData.selectedVariantId },
      external_product_id: { ecommerce: purchaseData.id },
      quantity: purchaseData.quantity,
      next_charge_scheduled_at: purchaseData.customerNextChargeDate,
      properties,
    },
    { commit: true },
  )
}

export async function frequencyToSellingPlanId(
  productId: string,
  variantId: string,
  frequency: number,
) {
  const products = await getCDNProducts()
  const product = products.find((p) => p[productId])
  if (!product) return null
  const variant = product[productId].variants.find(
    (v) => v.id.toString() === variantId,
  )
  if (!variant) return null
  const sellingPlanAllocations = variant.selling_plan_allocations
  const sellingPlans = product[productId].selling_plan_groups.flatMap(
    (spg) => spg.selling_plans,
  )
  const sellingPlan = sellingPlans
    .filter((sp) =>
      sellingPlanAllocations
        .map((spa) => spa.selling_plan_id)
        .includes(sp.selling_plan_id),
    )
    .find((sp) => {
      return sp.order_interval_frequency === frequency
    })

  if (!sellingPlan) return null
  return sellingPlan.selling_plan_id.toString()
}
